import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { icons, LogIn, RotateCcw } from 'lucide-react';
import { moodOptions, MoodType } from '@/data/moodActivities';
import { cn } from '@/lib/utils';

interface GuestMoodPromptProps {
  selectedMood: MoodType;
  onLogin: () => void;
  onReset: () => void;
}

const moodAccentButton: Record<MoodType, string> = {
  happy: 'bg-teal-600 hover:bg-teal-700 text-white',
  sad: 'bg-purple-600 hover:bg-purple-700 text-white',
  annoyed: 'bg-blue-600 hover:bg-blue-700 text-white',
  relaxed: 'bg-teal-600 hover:bg-teal-700 text-white',
};

const GuestMoodPrompt: React.FC<GuestMoodPromptProps> = ({ selectedMood, onLogin, onReset }) => {
  const selected = moodOptions.find(m => m.type === selectedMood)!;
  const others = moodOptions.filter(m => m.type !== selectedMood);
  const SelectedIcon = icons[selected.iconName as keyof typeof icons];

  return (
    <div className="flex flex-col md:flex-row items-center gap-8 max-w-2xl mx-auto">
      {/* Left: Selected mood large + other moods small */}
      <div className="flex flex-col items-center gap-4 shrink-0">
        <Card
          className={cn(
            'flex flex-col items-center justify-center gap-3 p-8 border-2 shadow-lg',
            selected.borderClass, selected.bgClass
          )}
        >
          <div className={cn('flex items-center justify-center w-20 h-20 rounded-full', selected.iconBg)}>
            {SelectedIcon && <SelectedIcon className={cn('h-11 w-11', selected.colorClass)} />}
          </div>
          <span className={cn('font-bold text-base text-gray-900')}>{selected.label}</span>
        </Card>

        {/* Other moods row */}
        <div className="flex items-center gap-3">
          {others.map((m) => {
            const Icon = icons[m.iconName as keyof typeof icons];
            return (
              <button
                key={m.type}
                onClick={onReset}
                className={cn(
                  'flex flex-col items-center gap-1 p-2 rounded-xl border transition-all hover:scale-105 cursor-pointer',
                  m.borderClass, 'bg-white'
                )}
                title={`Switch to ${m.label}`}
              >
                <div className={cn('flex items-center justify-center w-9 h-9 rounded-full', m.iconBg)}>
                  {Icon && <Icon className={cn('h-5 w-5', m.colorClass)} />}
                </div>
                <span className="text-[10px] text-gray-500 font-medium">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: Message + CTA */}
      <div className="flex-1 text-center md:text-left space-y-4">
        <p className="text-lg font-semibold text-gray-900">
          Hey, we've got a wellness activity for you! 🌿
        </p>
        <p className="text-sm text-gray-600 leading-relaxed">
          Please log in or sign up to access your personalized activity suggestion based on your mood.
        </p>
        <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start">
          <Button onClick={onLogin} className={cn(moodAccentButton[selectedMood], 'gap-2')}>
            <LogIn className="h-4 w-4" />
            Login / Sign Up
          </Button>
          <Button variant="outline" onClick={onReset} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Change Mood
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GuestMoodPrompt;
